//#region assets/vp-yotpo-reviews.ts
/**
* Hydrates Yotpo rating + review count into theme placeholders and loads
* live review cards into review-highlights carousels. Product pages use
* product-specific data; other pages use site-wide data.
*
* Looks for:
* - [data-vp-yotpo="rating"]  → formatted average (e.g. 4,8)
* - [data-vp-yotpo="count"]   → formatted total reviews
* - [data-vp-yotpo="caption"] → "4,8/5 van de 2.005 reviews"
* - [data-vp-yotpo="score"]   → same as rating (for large summary scores)
* - [data-vp-yotpo-stars]     → updates filled/muted star icons from average
* - [data-vp-yotpo-carousel]  → fills review-highlights cards from Yotpo API
*
* Boot node: #vp-yotpo-reviews-boot[data-app-key]
*/
var CACHE_KEY = "vp_yotpo_store_bottomline";
var REVIEWS_CACHE_KEY = "vp_yotpo_reviews_v3";
var CACHE_TTL_MS = 432e5;
var STAR_PATH = "M15.3862 2.33496H16.5864L19.4694 11.2068L28.798 11.2072L29.1689 12.3487L21.6221 17.8322L24.5045 26.7043L23.5335 27.4097L15.9863 21.9268L8.43913 27.4097L7.46812 26.7043L10.3504 17.8322L2.80371 12.3487L3.1746 11.2072L12.5031 11.2068L15.3862 2.33496Z";
var VERIFIED_ICON = `<span class="review-highlights__verified-icon" aria-hidden="true">
  <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" focusable="false">
    <path d="M26.4707 10.4702L13.0005 23.9405L5.53027 16.4702L6.47072 15.5298L13.0005 22.0596L25.5303 9.52979L26.4707 10.4702Z" fill="currentColor" class="bmwfcol"/>
    <title>checkmark_lt_48</title>
  </svg>
</span>`;
/**
* @param {number} score
* @param {string} [locale]
*/
function formatRating(score, locale = "nl-NL") {
	return Number(score).toLocaleString(locale, {
		minimumFractionDigits: 1,
		maximumFractionDigits: 1
	});
}
/**
* @param {number} count
* @param {string} [locale]
*/
function formatCount(count, locale = "nl-NL") {
	return Number(count).toLocaleString(locale);
}
/**
* @param {string} value
*/
function escapeHtml(value) {
	return String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
/**
* @returns {string|null}
*/
function detectAppKey() {
	const fromBoot = document.getElementById("vp-yotpo-reviews-boot")?.dataset?.appKey?.trim();
	if (fromBoot) return fromBoot;
	const settingsKey = window.yotpo_settings?.app_key || window.yotpoAppKey;
	if (typeof settingsKey === "string" && settingsKey.trim()) return settingsKey.trim();
	const scripts = document.querySelectorAll("script[src*=\"yotpo.com\"]");
	for (const script of scripts) {
		const src = script.getAttribute("src") || "";
		const loaderMatch = src.match(/yotpo\.com\/v1\/loader\/([^/?]+)/i);
		if (loaderMatch?.[1]) return loaderMatch[1];
		const staticMatch = src.match(/staticw2\.yotpo\.com\/([^/]+)\//i);
		if (staticMatch?.[1]) return staticMatch[1];
	}
	return null;
}
/**
* @param {unknown} bottomline
*/
function parseBottomline(bottomline) {
	const averageScore = Number(bottomline?.average_score);
	const totalReviews = Number(bottomline?.total_reviews ?? bottomline?.total_review);
	if (!Number.isFinite(averageScore) || !Number.isFinite(totalReviews)) throw new Error("yotpo_empty");
	return {
		averageScore,
		totalReviews
	};
}
/**
* @param {string} appKey
* @returns {Promise<{ averageScore: number, totalReviews: number }>}
*/
async function fetchBottomline(appKey) {
	const url = `https://api.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/products/yotpo_site_reviews/bottomline`;
	const response = await fetch(url, {
		method: "GET",
		credentials: "omit",
		headers: { Accept: "application/json" }
	});
	if (!response.ok) throw Object.assign(/* @__PURE__ */ new Error("yotpo_http"), { status: response.status });
	return parseBottomline((await response.json())?.response?.bottomline);
}
/**
* @param {string} appKey
* @param {number} perPage
* @param {string} [productId]
* @returns {Promise<{ reviews: Array<Record<string, unknown>>, stats: { averageScore: number, totalReviews: number } }>}
*/
async function fetchReviews(appKey, perPage, productId = "") {
	const params = new URLSearchParams({
		per_page: String(perPage),
		sort: "date",
		direction: "desc"
	});
	const url = `https://api.yotpo.com/v1/widget/${encodeURIComponent(appKey)}/products/${encodeURIComponent(productId || "yotpo_site_reviews")}/reviews.json?${params}`;
	const response = await fetch(url, {
		method: "GET",
		credentials: "omit",
		headers: { Accept: "application/json" }
	});
	if (!response.ok) throw Object.assign(/* @__PURE__ */ new Error("yotpo_http"), { status: response.status });
	const data = await response.json();
	const reviews = data?.response?.reviews;
	if (!Array.isArray(reviews)) throw new Error("yotpo_empty_reviews");
	return {
		reviews,
		stats: parseBottomline(data?.response?.bottomline)
	};
}
/**
* @returns {{ averageScore: number, totalReviews: number, cachedAt: number }|null}
*/
function readCache() {
	try {
		const raw = sessionStorage.getItem(CACHE_KEY);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object") return null;
		if (!Number.isFinite(parsed.averageScore) || !Number.isFinite(parsed.totalReviews)) return null;
		if (!Number.isFinite(parsed.cachedAt)) return null;
		if (Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
		return parsed;
	} catch {
		return null;
	}
}
/**
* @param {string} sourceKey
* @param {number} perPage
* @param {boolean} [allowExpired]
* @returns {{ reviews: Array<Record<string, unknown>>, stats: { averageScore: number, totalReviews: number }, cachedAt: number }|null}
*/
function readReviewsCache(sourceKey, perPage, allowExpired = false) {
	try {
		const raw = sessionStorage.getItem(`${REVIEWS_CACHE_KEY}:${sourceKey}`);
		if (!raw) return null;
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== "object") return null;
		if (!Array.isArray(parsed.reviews)) return null;
		if (!parsed.stats || !Number.isFinite(parsed.stats.averageScore) || !Number.isFinite(parsed.stats.totalReviews)) return null;
		if (parsed.perPage !== perPage) return null;
		if (!Number.isFinite(parsed.cachedAt)) return null;
		if (!allowExpired && Date.now() - parsed.cachedAt > CACHE_TTL_MS) return null;
		return parsed;
	} catch {
		return null;
	}
}
/**
* @param {{ averageScore: number, totalReviews: number }} stats
*/
function writeCache(stats) {
	try {
		sessionStorage.setItem(CACHE_KEY, JSON.stringify({
			averageScore: stats.averageScore,
			totalReviews: stats.totalReviews,
			cachedAt: Date.now()
		}));
	} catch {}
}
/**
* @param {string} sourceKey
* @param {{ reviews: Array<Record<string, unknown>>, stats: { averageScore: number, totalReviews: number } }} payload
* @param {number} perPage
*/
function writeReviewsCache(sourceKey, payload, perPage) {
	try {
		sessionStorage.setItem(`${REVIEWS_CACHE_KEY}:${sourceKey}`, JSON.stringify({
			reviews: payload.reviews,
			stats: payload.stats,
			perPage,
			cachedAt: Date.now()
		}));
	} catch {}
}
/**
* @param {number} score
*/
function buildStarsHtml(score) {
	const filled = Math.max(0, Math.min(5, Math.round(Number(score))));
	return Array.from({ length: 5 }, (_, index) => {
		return `<svg class="review-highlights__star${index >= filled ? " review-highlights__star--muted" : ""}" width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true"><path d="${STAR_PATH}" fill="currentColor"/><title>star_lt_48</title></svg>`;
	}).join("");
}
/**
* @param {Record<string, unknown>} review
*/
function getReviewImageUrl(review) {
	const images = review.images_data;
	if (!Array.isArray(images) || images.length === 0) return "";
	const image = images[0];
	if (!image || typeof image !== "object") return "";
	const original = image.original_url || image.thumb_url;
	return typeof original === "string" ? original : "";
}
/**
* @param {Record<string, unknown>} review
*/
function getReviewAuthor(review) {
	const user = review.user;
	if (!user || typeof user !== "object") return "Klant";
	const displayName = user.display_name;
	return typeof displayName === "string" && displayName.trim() ? displayName.trim() : "Klant";
}
/**
* @param {Record<string, unknown>} review
*/
function getReviewAvatarUrl(review) {
	const user = review.user;
	if (!user || typeof user !== "object") return "";
	const socialImage = user.social_image;
	return typeof socialImage === "string" ? socialImage : "";
}
/**
* @param {Record<string, unknown>} review
* @param {number} index
* @param {boolean} isActive
* @param {string} verifiedLabel
*/
function buildReviewCardHtml(review, index, isActive, verifiedLabel) {
	const score = Number(review.score);
	const rating = formatRating(Number.isFinite(score) ? score : 5);
	const authorName = escapeHtml(getReviewAuthor(review));
	const reviewText = escapeHtml(String(review.content || review.title || "").trim());
	const verified = review.verified_buyer === true;
	const avatarUrl = getReviewAvatarUrl(review);
	const imageUrl = getReviewImageUrl(review);
	const stars = buildStarsHtml(score);
	const avatarHtml = avatarUrl ? `<img src="${escapeHtml(avatarUrl)}" alt="" class="review-highlights__avatar-img" loading="lazy" width="48" height="48">` : "<div class=\"review-highlights__avatar-img review-highlights__avatar-img--placeholder\" aria-hidden=\"true\"></div>";
	const mediaHtml = imageUrl ? `<div class="review-highlights__media"><img src="${escapeHtml(imageUrl)}" alt="" class="review-highlights__img" loading="lazy" sizes="(min-width: 990px) 28vw, 78vw"></div>` : "";
	const expandedModifier = imageUrl ? "" : " review-highlights__expanded--text-only";
	const authorHtml = `<div class="review-highlights__author">
    <div class="review-highlights__avatar" aria-hidden="true">${avatarHtml}</div>
    <div class="review-highlights__author-meta">
      <p class="review-highlights__author-name">${authorName}</p>
      ${verified ? `<p class="review-highlights__verified">${VERIFIED_ICON}<span>${escapeHtml(verifiedLabel)}</span></p>` : ""}
    </div>
  </div>`;
	return `<article
    class="review-highlights__card${isActive ? " is-expanded" : ""}"
    role="listitem"
    tabindex="0"
    data-review-card
    data-vp-enter-item
    aria-expanded="${isActive ? "true" : "false"}"
    data-yotpo-review-id="${escapeHtml(String(review.id || index))}"
  >
    <div class="review-highlights__collapsed" data-collapsed${isActive ? " aria-hidden=\"true\"" : ""}>
      <div class="review-highlights__collapsed-top">${authorHtml}</div>
      <div class="review-highlights__rating">
        <span class="review-highlights__rating-score">${rating}</span>
        <div class="review-highlights__stars" aria-hidden="true">${stars}</div>
      </div>
    </div>
    <div class="review-highlights__expanded${expandedModifier}" data-expanded${isActive ? "" : " aria-hidden=\"true\""}>
      ${mediaHtml}
      <div class="review-highlights__expanded-body">
        ${reviewText ? `<p class="review-highlights__quote type-paragraph">${reviewText}</p>` : ""}
        <div class="review-highlights__expanded-footer">
          ${authorHtml}
          <div class="review-highlights__rating review-highlights__rating--inline">
            <span class="review-highlights__rating-score">${rating}</span>
            <div class="review-highlights__stars" aria-hidden="true">${stars}</div>
          </div>
        </div>
      </div>
    </div>
  </article>`;
}
/**
* @param {HTMLElement} carousel
* @param {Array<Record<string, unknown>>} reviews
*/
function renderReviewCarousel(carousel, reviews) {
	const track = carousel.querySelector("[data-track]");
	const section = carousel.closest("review-highlights");
	if (!track || !section) return;
	const limit = Math.min(3, Number.parseInt(carousel.dataset.reviewLimit || "3", 10) || 3);
	const verifiedLabel = carousel.dataset.verifiedLabel || "Verified customer";
	const selected = reviews.slice(0, Number.isFinite(limit) && limit > 0 ? limit : 6);
	const defaultActive = Math.floor(selected.length / 2);
	section.dataset.defaultActive = String(defaultActive);
	track.innerHTML = selected.map((review, index) => buildReviewCardHtml(review, index, index === defaultActive, verifiedLabel)).join("");
	carousel.classList.remove("is-loading");
	section.dispatchEvent(new CustomEvent("vp:yotpo-reviews-loaded", {
		bubbles: true,
		detail: {
			sectionId: section.getAttribute("data-section-id"),
			count: selected.length
		}
	}));
}
/**
* @param {HTMLElement} section
* @param {boolean} visible
*/
function setProductSectionVisibility(section, visible) {
	if (section.dataset.vpYotpoScope !== "product") return;
	section.hidden = !visible;
	if (visible && window.location.hash === `#${section.id}`) requestAnimationFrame(() => section.scrollIntoView({ block: "start" }));
}
/**
* @param {string} productId
*/
function findProductSummaries(productId) {
	return Array.from(document.querySelectorAll("[data-vp-yotpo-product-summary]")).filter((summary) => summary instanceof HTMLElement && summary.dataset.yotpoProductId === productId);
}
/**
* @param {string} productId
* @param {boolean} visible
*/
function setProductSummariesVisibility(productId, visible) {
	findProductSummaries(productId).forEach((summary) => {
		summary.hidden = !visible;
	});
}
/**
* @param {{ averageScore: number, totalReviews: number }} stats
* @param {string} productId
* @param {HTMLElement} reviewSection
*/
function applyProductStats(stats, productId, reviewSection) {
	applyStats(stats, reviewSection);
	findProductSummaries(productId).forEach((summary) => {
		applyStats(stats, summary);
		summary.hidden = false;
	});
}
/**
* @param {string} productId
* @param {number} [perPage]
*/
function readCachedProductStats(productId, perPage = 1) {
	let payload = readReviewsCache(`product:${productId}`, perPage);
	if (payload) return payload;
	for (const cachedPerPage of [3, 1]) {
		if (cachedPerPage === perPage) continue;
		payload = readReviewsCache(`product:${productId}`, cachedPerPage);
		if (payload) return payload;
	}
	return null;
}
/**
* @param {string} appKey
* @param {string} productId
*/
async function hydrateProductSummary(appKey, productId) {
	const perPage = 1;
	const sourceKey = `product:${productId}`;
	let payload = readCachedProductStats(productId, perPage);
	if (!payload) try {
		payload = await fetchReviews(appKey, perPage, productId);
		writeReviewsCache(sourceKey, payload, perPage);
	} catch (error) {
		payload = readCachedProductStats(productId, perPage) ?? readReviewsCache(sourceKey, perPage, true);
		if (!payload) throw error;
	}
	if (payload.stats.totalReviews <= 0 || payload.stats.averageScore <= 0) {
		setProductSummariesVisibility(productId, false);
		return;
	}
	findProductSummaries(productId).forEach((summary) => {
		applyStats(payload.stats, summary);
		summary.hidden = false;
	});
}
/**
* @param {string} appKey
*/
async function hydrateStandaloneProductSummaries(appKey) {
	const carouselProductIds = new Set(Array.from(document.querySelectorAll("[data-vp-yotpo-carousel][data-yotpo-product-id]")).filter((carousel) => carousel instanceof HTMLElement).map((carousel) => carousel.dataset.yotpoProductId?.trim()).filter((productId) => Boolean(productId)));
	const productIds = [...new Set(Array.from(document.querySelectorAll("[data-vp-yotpo-product-summary][data-yotpo-product-id]")).filter((summary) => summary instanceof HTMLElement).map((summary) => summary.dataset.yotpoProductId?.trim()).filter((productId) => Boolean(productId)).filter((productId) => !carouselProductIds.has(productId)))];
	if (productIds.length === 0) return;
	await Promise.all(productIds.map(async (productId) => {
		try {
			await hydrateProductSummary(appKey, productId);
		} catch (error) {
			console.warn("[vp-yotpo] product summary unavailable", productId, error);
		}
	}));
}
/**
* @param {string} appKey
* @param {HTMLElement} carousel
*/
async function hydrateReviewCarousel(appKey, carousel) {
	const section = carousel.closest("review-highlights");
	if (!(section instanceof HTMLElement)) return;
	const requestedLimit = Number.parseInt(carousel.getAttribute("data-review-limit") || "3", 10);
	const perPage = Math.min(3, Number.isFinite(requestedLimit) && requestedLimit > 0 ? requestedLimit : 3);
	const productId = carousel.dataset.yotpoProductId?.trim() || "";
	const sourceKey = productId ? `product:${productId}` : "site";
	let payload = readReviewsCache(sourceKey, perPage);
	if (!payload) try {
		payload = await fetchReviews(appKey, perPage, productId);
		writeReviewsCache(sourceKey, payload, perPage);
	} catch (error) {
		payload = readReviewsCache(sourceKey, perPage, true);
		if (!payload) throw error;
	}
	if (productId) {
		if (payload.stats.totalReviews <= 0 || payload.stats.averageScore <= 0 || payload.reviews.length === 0) {
			setProductSectionVisibility(section, false);
			setProductSummariesVisibility(productId, false);
			return;
		}
		applyProductStats(payload.stats, productId, section);
	}
	renderReviewCarousel(carousel, payload.reviews);
	setProductSectionVisibility(section, true);
	if (typeof section.refreshCards === "function") section.refreshCards();
}
/**
* @param {string} appKey
*/
async function hydrateReviewCarousels(appKey) {
	const carousels = Array.from(document.querySelectorAll("[data-vp-yotpo-carousel]")).filter((carousel) => carousel instanceof HTMLElement);
	if (carousels.length === 0) return;
	await Promise.all(carousels.map(async (carousel) => {
		try {
			await hydrateReviewCarousel(appKey, carousel);
		} catch (error) {
			console.warn("[vp-yotpo] review carousel unavailable", error);
			carousel.classList.remove("is-loading");
			const section = carousel.closest("review-highlights");
			if (section instanceof HTMLElement) setProductSectionVisibility(section, false);
		}
	}));
}
/**
* @param {ParentNode} root
* @param {string} selector
*/
function findStatsTargets(root, selector) {
	const targets = Array.from(root.querySelectorAll(selector));
	if (root !== document) return targets;
	return targets.filter((target) => !target.closest("[data-vp-yotpo-scope=\"product\"]") && !target.closest("[data-vp-yotpo-product-summary]"));
}
/**
* @param {{ averageScore: number, totalReviews: number }} stats
* @param {ParentNode} [root]
*/
function applyStars(stats, root = document) {
	const filled = Math.max(0, Math.min(5, Math.round(stats.averageScore)));
	findStatsTargets(root, "[data-vp-yotpo-stars]").forEach((group) => {
		group.querySelectorAll(".review-highlights__star, .vp-yotpo-star").forEach((star, index) => {
			star.classList.toggle("review-highlights__star--muted", index >= filled);
			star.classList.toggle("vp-yotpo-star--muted", index >= filled);
		});
		const host = group.closest("[aria-label]") || group;
		if (host instanceof HTMLElement && host.hasAttribute("aria-label")) host.setAttribute("aria-label", `Beoordeling van dit product is ${formatRating(stats.averageScore)} van 5`);
	});
}
/**
* @param {{ averageScore: number, totalReviews: number }} stats
* @param {ParentNode} [root]
*/
function applyStats(stats, root = document) {
	const rating = formatRating(stats.averageScore);
	const count = formatCount(stats.totalReviews);
	const caption = `${rating}/5 van de ${count} reviews`;
	findStatsTargets(root, "[data-vp-yotpo=\"rating\"], [data-vp-yotpo=\"score\"]").forEach((el) => {
		el.textContent = rating;
	});
	findStatsTargets(root, "[data-vp-yotpo=\"count\"]").forEach((el) => {
		el.textContent = count;
	});
	findStatsTargets(root, "[data-vp-yotpo=\"caption\"]").forEach((el) => {
		el.textContent = caption;
	});
	applyStars(stats, root);
	(root === document ? document.documentElement : root).dispatchEvent(new CustomEvent(root === document ? "vp:yotpo-store-stats" : "vp:yotpo-product-stats", { detail: {
		...stats,
		rating,
		count,
		caption
	} }));
}
async function init() {
	const appKey = detectAppKey();
	if (!appKey) return;
	let stats = readCache();
	if (!stats) try {
		stats = await fetchBottomline(appKey);
		writeCache(stats);
	} catch (error) {
		console.warn("[vp-yotpo] store bottomline unavailable", error);
	}
	if (stats) {
		applyStats(stats);
		document.documentElement.dataset.vpYotpoReady = "1";
	}
	await hydrateReviewCarousels(appKey);
	await hydrateStandaloneProductSummaries(appKey);
}
if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init, { once: true });
else init();
//#endregion
